import { DataTable } from '@peritasai/ui-components';
import type { GridColDef, GridPaginationModel } from '@mui/x-data-grid-premium';
import type { AssetCatalogItemEntity } from '@peritasai/repository';
import { useTranslation } from 'react-i18next';
import { formatAssetType } from '../../utils/formatAssetType';
import { renderTableCell } from '../shared/renderTableCell';

const getColumns = (
  t: (key: string) => string
): GridColDef<AssetCatalogItemEntity>[] => [
  {
    field: 'name',
    headerName: t('pages.entityManagement.assetCatalogTable.columnName'),
    flex: 1,
    renderCell: (params) => renderTableCell(params.value, true),
  },
  {
    field: 'type',
    headerName: t('pages.entityManagement.assetCatalogTable.columnType'),
    flex: 1,
    renderCell: (params) =>
      renderTableCell(
        params.value === null ? null : `${formatAssetType(params.value)} ${params.row.trayType !== null ? `(${t(`pages.entityManagement.assetCatalogDrawer.trayTypeOptions.${params.row.trayType}`)})` : ''}`
      ),
  },
  {
    field: 'manufacturer',
    headerName: t('pages.entityManagement.assetCatalogTable.columnManufacturer'),
    flex: 1,
    renderCell: (params) => renderTableCell(params.value),
  },
  {
    field: 'productNumber',
    headerName: t('pages.entityManagement.assetCatalogTable.columnProductNumber'),
    flex: 1,
    renderCell: (params) => renderTableCell(params.value),
  },
  {
    field: 'description',
    headerName: t('pages.entityManagement.assetCatalogTable.columnDescription'),
    flex: 1,
    renderCell: (params) => renderTableCell(params.value),
  },
  {
    field: 'metadata',
    headerName: t('pages.entityManagement.assetCatalogTable.columnMetadata'),
    flex: 1.5,
    headerClassName: 'last-column-header',
    renderCell: (params) =>
      renderTableCell(params.value != null ? JSON.stringify(params.value) : null),
  },
];

interface AssetCatalogTableProps {
  rows: AssetCatalogItemEntity[];
  rowCount: number;
  isLoading: boolean;
  hasSearch: boolean;
  paginationModel: GridPaginationModel;
  onPaginationModelChange: (model: GridPaginationModel) => void;
  onRowClick: (row: AssetCatalogItemEntity) => void;
}

export const AssetCatalogTable = ({
  rows,
  rowCount,
  isLoading,
  hasSearch,
  paginationModel,
  onPaginationModelChange,
  onRowClick,
}: AssetCatalogTableProps) => {
  const { t } = useTranslation(['periverse']);
  const columns = getColumns(t);

  return (
    <DataTable
      rows={rows}
      columns={columns}
      getRowId={(row) => row.id}
      rowCount={rowCount}
      isLoading={isLoading}
      hasSearch={hasSearch}
      paginationModel={paginationModel}
      onPaginationModelChange={onPaginationModelChange}
      onRowClick={onRowClick}
      emptyMessage={t('pages.entityManagement.table.noRows', {
        entityName: t('pages.entityManagement.items.trackedAssetCatalog'),
      })}
      skeletonColumnCount={6}
    />
  );
};
